import React, { useState, useEffect } from 'react';
import styles from './PartnerPanel.module.scss';
import {Compass, Timer, Trophy, Pencil, Check, X } from 'lucide-react';

const PartnerPanel = ({ gameName, totalUsers, globalStats, finishedRuns, loadMoreRuns, hasMoreRuns, period, setPeriod, companyInfo, onSaveCompanyInfo, gameCode, onRegenerateCode }) => {

  // const [period, setPeriod] = useState('week');

  // --- état édition infos partenaire ---
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [form, setForm] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: ''
  });

  //Pour la régénération du code
  const [regenMessage, setRegenMessages] = useState(null);
  const [regenLoading, setRegenLoading] = useState(false);

  useEffect(() => {
    // hydrate depuis props quand on arrive (ou quand ça change)
    setForm({
      company_name: companyInfo?.company_name || '',
      company_address: companyInfo?.company_address || '',
      company_phone: companyInfo?.company_phone || '',
      company_email: companyInfo?.company_email || ''
    });
  }, [companyInfo]);

  // ----------- HANDLERS -------------

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const onCodeRegen = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Êtes-vous sûr de vouloir générer un nouveau code ?")) return;

    try {
      const newCode = await onRegenerateCode?.();
      setRegenLoading(true);
      setRegenMessages({ type: 'success', message: 'Code mis à jour !' });
    } catch {
      setRegenMessages({ type: 'error', message: 'Erreur serveur.' });
    } finally {
      setRegenLoading(false);
    }
  }

  const validate = () => {
    // email optionnel mais s’il est rempli, on valide
    if (form.company_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.company_email)) {
      setMsg({ type: 'error', text: "Email invalide." });
      return false;
    }
    // téléphone optionnel (laisse libre), tu peux durcir si tu veux:
    // if (form.company_phone && !/^\+?[0-9 ()\-._]{6,}$/.test(form.company_phone)) { ... }
    return true;
    };

  const startEdit = () => {
    setMsg(null);
    setEditMode(true);
  };

  const cancelEdit = () => {
    // revert to props values
    setForm({
      company_name: companyInfo?.company_name || '',
      company_address: companyInfo?.company_address || '',
      company_phone: companyInfo?.company_phone || '',
      company_email: companyInfo?.company_email || ''
    });
    setMsg(null);
    setEditMode(false);
  };

  const save = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      setMsg(null);
      const ok = await onSaveCompanyInfo?.({
        company_name: form.company_name || null,
        company_address: form.company_address || null,
        company_phone: form.company_phone || null,
        company_email: form.company_email || null
      });
      if (ok === false) {
        setMsg({ type: 'error', text: "Échec de la sauvegarde." });
        return;
      }
      setMsg({ type: 'success', text: "Informations mises à jour." });
      setEditMode(false);
    } catch (e) {
      setMsg({ type: 'error', text: "Erreur lors de la sauvegarde." });
    } finally {
      setSaving(false);
    }
  };

  const hasAnyInfo =
    !!(companyInfo?.company_name || companyInfo?.company_address || companyInfo?.company_phone || companyInfo?.company_email);

  // ----------- HELPERS --------------

  function formatDate(dateStr) {
    if (!dateStr) return '';

    const date = new Date(dateStr);
    const options = { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' };
    // Utilise toLocaleString pour avoir les abréviations correctes, comme "juin" → "juin." ou "jun." selon navigateur
    return date.toLocaleString('fr-FR', options).replace(/\s/g, ' ').replace(/(\w+)\./, '$1.');
  }

  function formatDuration(seconds) {
    const hrs = Math.floor(seconds / 3600);
    const min = Math.floor((seconds % 3600) / 60);
    const sec = seconds % 60;

    const pad = (val) => String(val).padStart(2, '0');

    return `${pad(hrs)}:${pad(min)}:${pad(sec)}`;
  }

  function scoreCalc(totalTime, nbCompass, maxCompass = 30) {
    const K = 900;                  // Contrôle la pente
    const baseTimeScore = 150;      // Score max temps
    const scoreTime = baseTimeScore * Math.exp(-totalTime / K);

    const normalizedCompass = Math.min(nbCompass / maxCompass, 1); // Entre 0 et 1
    const compassScore = normalizedCompass * 100;                  // Score max boussoles

    return Math.round(scoreTime + compassScore);
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.globalStats}>
        <span>Utilisateurs inscrits sur Hatannapp : {totalUsers}</span>
      </div>
      <hr className={styles.hr} />

      <h2 className={styles.gameTitle}>{gameName}</h2>
      <hr className={styles.hr} />
      
      <header className={styles.header}>
        <h3 className={styles.title}>Vue d'ensemble</h3>
        <select value={period} onChange={e => setPeriod(e.target.value)}>
          <option value="day">Jour</option>
          <option value="week">Semaine</option>
          <option value="month">Mois</option>
          <option value="year">Année</option>
        </select>
        
      </header>

      {/* <section className={styles.gamesSection}>
        <h2>{gameName}</h2>
        <ul>
          <li>Sessions lancées : {globalStats?.sessions ?? '...'}</li>
          <li>Joueurs estimés : {globalStats?.estimated_players ?? '...'}</li>
        </ul>
      </section> */}
      <section className={styles.gamesSection}>
        <div className={styles.statsCard}>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Sessions lancées</span>
            <span className={styles.statValue}>{globalStats?.sessions ?? '...'}</span>
          </div>

          <div className={styles.statArrow}>
            ➝
          </div>

          <div className={styles.statItem}>
            <span className={styles.statLabel}>Joueurs estimés</span>
            <span className={styles.statValue}>{globalStats?.estimated_players ?? '...'}</span>
          </div>
        </div>
      </section>
      
      <hr className={styles.hr} />

      <section className={styles.completedRuns}>
        <h2 className={styles.title}>Derniers parcours achevés</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Joueur</th>
              {/* <th>
                <div className={styles.desktopOnly}><Timer size={22}/></div>
                <div className={styles.smallTag}>Temps</div>
              </th>
              <th>
                <div className={styles.desktopOnly}><Compass size={20}/></div>
                <div className={styles.smallTag}>Boussoles</div>
              </th>
              <th>
                <div className={styles.desktopOnly}><Trophy size={20}/></div>
                <div className={styles.smallTag}>Score</div>
              </th> */}
            </tr>
          </thead>
          <tbody>
            {finishedRuns.map(run => (
              <tr key={run.id}>
                <td>{formatDate(run.achieved_at)}</td>
                <td>
                  <div className={styles.pseudoLine}>{run.pseudo}</div>
                  <div className={styles.emailLine}>{run.email}</div>
                  <div className={styles.mobileStats}>
                    <div className={styles.statCell}><strong className={styles.duration}><Timer size={20}/></strong> <span className={styles.duration}>{formatDuration(run.duration)}</span></div>
                    <div className={styles.statCell}><strong className={styles.compass}><Compass size={18}/></strong> <span className={styles.compass}>{run.total_compass}</span></div>
                    <div className={styles.statCell}><strong className={styles.score}><Trophy size={18}/></strong> <span className={styles.score}>{scoreCalc(run.duration, run.total_compass)}</span></div>
                  </div>
                </td>
                {/* Colonnes desktop visibles uniquement en grand écran */}
                {/* <td className={styles.desktopOnly}>{formatDuration(run.duration)}</td>
                <td className={styles.desktopOnly}>{run.total_compass}</td>
                <td className={styles.desktopOnly}>{scoreCalc(run.duration, run.total_compass)}</td> */}
              </tr>
            ))}
          </tbody>
        </table>
        {hasMoreRuns && (
          <button className={styles.showMore} onClick={loadMoreRuns}>Charger plus</button>
        )}
        {!hasMoreRuns && (
          <p className={styles.noMore}>Aucun autre parcours à afficher.</p>
        )}
      </section>

      {/* <section className={styles.topPlayers}>
        <h2>Top 10 joueurs</h2>
        <ol>
          {topPlayers.map(player => (
            <li key={player.id}>{player.pseudo} - {player.score}</li>
          ))}
        </ol>
      </section> */}

      {/* <section className={styles.languageCountryStats}>
        <h2>Langues / Pays</h2>
        <div>
          <strong>Par langue :</strong>
          <ul>
            {Object.entries(globalStats.byLanguage).map(([lang, count]) => (
              <li key={lang}>{lang}: {count}</li>
            ))}
          </ul>
        </div>
        <div>
          <strong>Par pays :</strong>
          <ul>
            {Object.entries(globalStats.byCountry).map(([country, count]) => (
              <li key={country}>{country}: {count}</li>
            ))}
          </ul>
        </div>
      </section> */}

      <section className={styles.companySection}>
        <h2 className={styles.title}>Informations publiques de l'entreprise</h2>

        {!editMode && hasAnyInfo && (
          <div className={styles.companyCard}>
            <dl className={styles.companyList}>
              <div><dt>Entreprise</dt><dd>{companyInfo?.company_name || '—'}</dd></div>
              <div><dt>Adresse</dt><dd>{companyInfo?.company_address || '—'}</dd></div>
              <div><dt>Téléphone</dt><dd>{companyInfo?.company_phone || '—'}</dd></div>
              <div><dt>Email</dt><dd>{companyInfo?.company_email || '—'}</dd></div>
            </dl>

            {msg && (
              <p className={msg.type === 'error' ? styles.error : styles.success}>
                {msg.text}
              </p>
            )}

            <div className={styles.actionsRow}>
              <button className={styles.outlineButton} onClick={startEdit}>
                <Pencil size={16} /> Modifier
              </button>
            </div>
          </div>
        )}

        {!editMode && !hasAnyInfo && (
          <div className={styles.companyEmpty}>
            <p>Vous n’avez pas encore renseigné les informations de votre structure.</p>
            <button className={styles.primaryButton} onClick={startEdit}>
              Renseigner
            </button>
          </div>
        )}

        {editMode && (
          <div className={styles.companyFormCard}>
            <div className={styles.formGrid}>
              <label>
                <span>Nom de l’entreprise</span>
                <input
                  name="company_name"
                  type="text"
                  value={form.company_name}
                  onChange={onChange}
                  placeholder="Atelier Hatann"
                />
              </label>

              <label>
                <span>Adresse</span>
                <textarea
                  name="company_address"
                  value={form.company_address}
                  onChange={onChange}
                  placeholder="12 rue des Trésors, 35000 Rennes, France"
                  rows={3}
                />
              </label>

              <label>
                <span>Téléphone</span>
                <input
                  name="company_phone"
                  type="tel"
                  value={form.company_phone}
                  onChange={onChange}
                  placeholder="+33 2 99 00 00 00"
                />
              </label>

              <label>
                <span>Email</span>
                <input
                  name="company_email"
                  type="email"
                  value={form.company_email}
                  onChange={onChange}
                  placeholder="contact@exemple.com"
                />
              </label>
            </div>

            {msg && (
              <p className={msg.type === 'error' ? styles.error : styles.success}>
                {msg.text}
              </p>
            )}

            <div className={styles.actionsRow}>
              <button className={styles.cancelButton} onClick={cancelEdit} disabled={saving}>
                <X size={16} /> Annuler
              </button>
              <button className={styles.saveButton} onClick={save} disabled={saving}>
                <Check size={16} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}
      </section>

      <hr className={styles.hr} />
      <section className={styles.codeBlock}>

        <span className={styles.code}>code d'accès: <strong>{gameCode}</strong></span>
        <button
          type="button"
          className={styles.regenButton}
          onClick={onCodeRegen}
          disabled={regenLoading}
          aria-busy={regenLoading}
        >
          {regenLoading ? 'Régénération…' : 'Régénérer'}
        </button>
        {regenMessage && (
          <p aria-live="polite" className={regenMessage.type === 'error' ? styles.error : styles.success}>
            {regenMessage.text}
          </p>
        )}
      </section>
    </div>
  );
};

export default PartnerPanel;

